import React, { useEffect, useState, useRef } from "react";
import "firebase/firestore";

import { firebaseFirestore } from "./firebase";

type ID = string;

export interface GentleRequest {
  id: ID;
  published: boolean;
  creationDate: firebase.firestore.Timestamp;
  responseCount?: number;
  requesterID?: ID;
  requestMessage: string;
  publishFailures?: string[];
}

function RequestsTable() {
  const [requests, setRequests] = useState<GentleRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const requestsListener = useRef<Function>();

  useEffect(() => {
    requestsListener.current = firebaseFirestore
      .collection("requests")
      .orderBy("creationDate", "desc")
      .limit(50)
      .onSnapshot(
        (snapshot) => _handleSnapshot(snapshot),
        (error) => console.error("Error fetching requests:", error)
      );

    return () => {
      if (requestsListener.current) {
        requestsListener.current();
      }
    };
  }, []);

  const _handleSnapshot = (
    snapshot: firebase.firestore.QuerySnapshot
  ): void => {
    const docs = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        published: data.published ?? false,
        creationDate: data.creationDate,
        responseCount: data.responseCount ?? 0,
        requesterID: data.requesterID ?? "",
        requestMessage: data.requestMessage ?? "No message provided",
        publishFailures: data.publishFailures ?? [],
      } as GentleRequest;
    });
    setRequests(docs);
    setLoading(false);
  };

  const togglePublished = async (requestID: ID, published: boolean) => {
    if (
      published &&
      !window.confirm("Are you sure you want to publish this?")
    ) {
      return;
    }

    try {
      await firebaseFirestore.collection("requests").doc(requestID).update({
        published,
      });
      console.log(`Request ${requestID} updated to ${published ? "Published" : "Unpublished"}`);
    } catch (error) {
      console.error("Error updating request:", error);
    }
  };

  return (
    <>
      <h1 className="title">Unpublished Requests</h1>
      <p className="subtitle is-6">Ordered by most recent. Limit 50.</p>
      {loading ? (
        <div>Loading...</div>
      ) : requests.length ? (
        <table className="table is-striped is-hoverable is-fullwidth">
          <thead>
            <tr>
              <th>#</th>
              <th>Meta</th>
              <th>Message</th>
              <th>Failures</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request, index) => (
              <tr key={request.id}>
                <td>{index + 1}</td>
                <td>
                  <strong>
                    {request.creationDate?.toDate().toLocaleDateString()}
                  </strong>
                  <br />
                  <span className="has-text-grey">
                    {request.creationDate?.toDate().toLocaleTimeString()}
                  </span>
                </td>
                <td>{request.requestMessage}</td>
                <td>
                  {request.publishFailures?.length ? (
                    <ol className="content is-small">
                      {request.publishFailures.map((failure, idx) => (
                        <li key={idx}>{failure}</li>
                      ))}
                    </ol>
                  ) : (
                    "n/a"
                  )}
                </td>
                <td>
                  <span className={`tag ${request.published ? "is-warning" : ""}`}>
                    {request.published ? "Published" : "Unpublished"}
                  </span>
                </td>
                <td>
                  <button
                    className={`button is-small ${request.published ? "" : "is-danger"}`}
                    onClick={() => togglePublished(request.id, !request.published)}
                  >
                    {request.published ? "Unpublish" : "Publish"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div>No unpublished requests found.</div>
      )}
    </>
  );
}

export default RequestsTable;
